import type { ReactNode } from "react";
import type { TooltipProps } from "recharts";
import { formatNumber } from "@/lib/format";

/**
 * Tooltip chrome shared by every chart: chart surface, hairline border, ink
 * text, optional bold title line.
 */
export function TooltipShell({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  return (
    <div
      className="rounded-md border px-3 py-2 text-xs shadow-sm"
      style={{
        background: "var(--viz-surface)",
        borderColor: "var(--viz-grid)",
        color: "var(--viz-ink)",
      }}
    >
      {title !== undefined ? <div className="mb-1 font-medium">{title}</div> : null}
      {children}
    </div>
  );
}

/**
 * One tooltip row. Series identity comes from the colored key beside the
 * label (a line stroke or a bar swatch), never from coloring the text; a
 * row without a color keeps its indent so values stay aligned.
 */
export function TooltipRow({
  color,
  kind = "line",
  label,
  value,
}: {
  color?: string;
  kind?: "line" | "bar";
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span
        aria-hidden
        className={
          kind === "bar"
            ? "inline-block h-2 w-2 rounded-sm"
            : "inline-block h-0.5 w-3 rounded-full"
        }
        style={{ background: color ?? "transparent" }}
      />
      <span style={{ color: "var(--viz-ink-secondary)" }}>{label}</span>
      <span className="ml-auto pl-3 font-medium">{value}</span>
    </div>
  );
}

/** Default Recharts tooltip: one row per payload series, numbers formatted. */
export function ChartTooltip({
  active,
  payload,
  label,
  labelFormatter,
}: TooltipProps<number, string> & {
  labelFormatter?: (label: string) => string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const title =
    label === undefined
      ? undefined
      : labelFormatter
        ? labelFormatter(String(label))
        : String(label);
  return (
    <TooltipShell title={title}>
      {payload.map((entry) => (
        <TooltipRow
          key={entry.dataKey as string}
          color={entry.color}
          label={String(entry.name ?? entry.dataKey ?? "")}
          value={formatNumber(entry.value ?? 0)}
        />
      ))}
    </TooltipShell>
  );
}
